import { ChangeEvent, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Button, TextArea } from 'components/core';
import { builderEditsState, builderOutputState } from 'states';
import { useAdvancedAccess } from 'molecules/kpiBuilder/hooks/useAdvancedAccess';
import { editError } from 'helpers/kpiBuilder/editError';
import { OUTLINE_SMALL } from './buttonProps';

type Kind = 'query' | 'config';

interface GeneratedJsonProps {
  kind: Kind;
}

interface JsonEditorProps {
  kind: Kind;
  generated: string;
}

const LABELS = { query: 'generated query', config: 'generated config' };
const HINT = 'Changing an answer in the builder replaces your edit.';

// The text area itself: a valid edit is used when saving, an invalid one is only kept on screen.
const JsonEditor = ({ kind, generated }: JsonEditorProps) => {
  const [edits, setEdits] = useRecoilState(builderEditsState);
  const edited = edits[kind];
  const text = edited ?? generated;
  const [draft, setDraft] = useState(text);
  const error = draft === text ? '' : editError(kind, draft);

  // Follow the generated or reset text whenever it changes underneath.
  useEffect(() => setDraft(text), [text]);

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value);
    if (!editError(kind, e.target.value)) setEdits({ ...edits, [kind]: e.target.value });
  };
  const reset = () => {
    setEdits({ ...edits, [kind]: null });
    setDraft(generated);
  };

  return (
    <div className="mt-2">
      <TextArea rows={14} showTitle={false} title="" value={draft} onChange={onChange} spellCheck={false} aria-label={LABELS[kind]} />
      {error && <div className="text-danger small mt-1" role="alert">{error}</div>}
      <div className="d-flex align-items-center gap-3 mt-2">
        <span className="small text-muted">{edited !== null ? `Edited by hand. ${HINT}` : `You can edit this directly. ${HINT}`}</span>
        {edited !== null && (
          <Button {...OUTLINE_SMALL} onClick={reset}>
            Reset to generated
          </Button>
        )}
      </div>
    </div>
  );
};

// What the builder generated, for people allowed to see JSON. It can be edited here.
export const GeneratedJson = ({ kind }: GeneratedJsonProps) => {
  const [open, setOpen] = useState(false);
  const allowed = useAdvancedAccess();
  const output = useRecoilValue(builderOutputState);
  if (!allowed || !output) return null;

  const generated = JSON.stringify(kind === 'query' ? JSON.parse(output.queryString) : output.config, null, 2);
  const toggle = () => {
    setOpen(!open);
  };

  return (
    <div className="mt-4">
      <Button {...OUTLINE_SMALL} onClick={toggle}>
        {open ? 'Hide' : 'View or edit'} {LABELS[kind]}
      </Button>
      {open && <JsonEditor kind={kind} generated={generated} />}
    </div>
  );
};
